import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Container, CssBaseline, Grid, Tab, Tabs, ThemeProvider, Typography } from '@mui/material';
import { useSelector } from 'react-redux';

import theme from '../../theme';
import Topbar from '../Topbar';
import OrganizationFilters from '../Dashboard/OrganizationFilters';
import { dashboardTokens as t } from '../Dashboard/tokens';

import NineBoxGrid from '../NineBoxGrid';
import { CAPACITY_CONFIG, FUTURE_POTENTIAL_CONFIG } from '../nineBoxConfig';
import { getWithParams } from '../../api';
import Loading from '../common/Loading';
import { withLanguage } from '../common/Texts';
import { getText } from '../common/MultiLanguageText';
import {
    getPositionMatch,
    getFuturePotentialCategory,
    LOW, MEDIUM, HIGH,
} from '../../utils/employees';
import { getPotentialTalentArray, curvePickForReply } from '../../Jacques';
import { DEFAULT_POTENTIAL_RULESET } from '../../utils/potentialDSL';
import { toTitleCase } from '../../utils/toTitleCase';

function mapPerformance(value) {
    if (value === 1) return LOW;
    if (value === 2) return MEDIUM;
    if (value === 3) return HIGH;
    return null;
}

export default function NineBox() {
    const loggedIn = useSelector((s) => s.user.loggedIn);
    const company = useSelector((s) => s.user.company);
    const language = useSelector((s) => s.user.language);
    const tr = useMemo(() => withLanguage(language), [language]);

    const [filters, setFilters] = useState({
        slideBU: [], slideArea: [], slidePos: [], slideIG: [],
    });
    // El estado se escribe solo desde los callbacks del fetch: `loading` se deriva
    // comparando la petición pedida (requestKey) con la ya resuelta (result.key).
    const [result, setResult] = useState({ key: null, employees: [] });
    const [tab, setTab] = useState(0);

    const setFilter = (key) => (event) => {
        setFilters((prev) => ({ ...prev, [key]: event.target.value }));
    };

    const handleClearAll = () => {
        setFilters({ slideBU: [], slideArea: [], slidePos: [], slideIG: [] });
    };

    const positions = company?.positions;
    const ruleset = company?.potential_ruleset || DEFAULT_POTENTIAL_RULESET;

    const params = useMemo(() => {
        const p = {};
        if (filters.slideBU.length) p.business_units = filters.slideBU;
        if (filters.slideArea.length) p.departments = filters.slideArea;
        if (filters.slidePos.length) p.positions = filters.slidePos;
        if (filters.slideIG.length) p.internal_groups = filters.slideIG;
        return p;
    }, [filters]);

    // Incluye positions/ruleset porque el mapeo de la respuesta depende de ellos:
    // si la company llega después, la petición se repite y `loading` vuelve a true.
    const requestKey = useMemo(
        () => JSON.stringify([params, positions ?? null, ruleset]),
        [params, positions, ruleset],
    );

    useEffect(() => {
        if (!loggedIn) return undefined;

        const controller = new AbortController();
        getWithParams('/reply', params, { signal: controller.signal })
            .then((response) => {
                const results = response.data;
                const out = [];
                for (const result of results) {
                    const level = result.level_calc != null ? result.level_calc : result.level_label;
                    const sublevel = result.level_calc != null ? result.sublevel_calc : result.sublevel_label;
                    if (!level || !sublevel) continue;

                    const position = positions?.find((p) => p.id === result.position);
                    if (!position) continue;

                    const potential = getPositionMatch(position, level, sublevel);
                    const performance = mapPerformance(result.performance);

                    let futurePotential = null;
                    try {
                        const [nivelPotCurva, curva] = getPotentialTalentArray(
                            result.age_at_reply, level + sublevel, (key) => key,
                            curvePickForReply(result.created_date),
                        );
                        futurePotential = getFuturePotentialCategory(nivelPotCurva, curva, ruleset);
                    } catch {
                        // leave null
                    }

                    out.push({
                        id: result.id,
                        name: toTitleCase(result.name),
                        potential,
                        futurePotential,
                        performance,
                    });
                }
                setResult({ key: requestKey, employees: out });
            })
            .catch((err) => {
                if (err?.name === 'AbortError') return;
                console.error('Error fetching replies:', err);
                setResult({ key: requestKey, employees: [] });
            });
        return () => controller.abort();
    }, [loggedIn, params, requestKey, positions, ruleset]);

    const employees = result.employees;
    const loading = loggedIn && result.key !== requestKey;

    const getEmployeesByCapacity = useCallback(
        (potential, performance) => employees.filter(
            (e) => e.potential === potential && e.performance === performance,
        ),
        [employees],
    );

    const getEmployeesByFuturePotential = useCallback(
        (potential, performance) => employees.filter(
            (e) => e.futurePotential === potential && e.performance === performance,
        ),
        [employees],
    );

    const companyName = company ? getText(company, 'name', language) : '';

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline/>
            <Box sx={{ minHeight: '100vh', bgcolor: t.color.bgSub }}>
                <Topbar/>
                <Container maxWidth={false} sx={{ py: { xs: 3, md: 4 } }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography component="h1" sx={{
                            fontSize: 24, fontWeight: 700, color: t.color.fg, lineHeight: 1.2,
                        }}>
                            {tr('nine_box')}
                        </Typography>
                        {companyName && (
                            <Typography sx={{ fontSize: 13, color: t.color.fgMuted, mt: 0.5 }}>
                                {companyName}
                            </Typography>
                        )}
                    </Box>

                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12 }}>
                            <OrganizationFilters
                                language={language}
                                buRange={company?.business_units ?? []}
                                areaRange={company?.departments ?? []}
                                posRange={company?.positions ?? []}
                                igRange={company?.grupos_internos ?? []}
                                slideBU={filters.slideBU}
                                slideArea={filters.slideArea}
                                slidePos={filters.slidePos}
                                slideIG={filters.slideIG}
                                onChangeBU={setFilter('slideBU')}
                                onChangeArea={setFilter('slideArea')}
                                onChangePos={setFilter('slidePos')}
                                onChangeIG={setFilter('slideIG')}
                                onClearAll={handleClearAll}
                            />
                        </Grid>
                    </Grid>

                    <Tabs
                        value={tab}
                        onChange={(_, v) => setTab(v)}
                        indicatorColor="primary"
                        textColor="primary"
                        sx={{ mb: 3 }}
                    >
                        <Tab label={tr('capacity')}/>
                        <Tab label={tr('potential_future')}/>
                    </Tabs>

                    <Box sx={{ position: 'relative' }}>
                        <Loading loading={loading} language={language}/>
                        <Box sx={{ opacity: loading ? 0.1 : 1 }}>
                            {tab === 0 && (
                                <NineBoxGrid
                                    getEmployees={getEmployeesByCapacity}
                                    language={language}
                                    config={CAPACITY_CONFIG}
                                />
                            )}
                            {tab === 1 && (
                                <NineBoxGrid
                                    getEmployees={getEmployeesByFuturePotential}
                                    language={language}
                                    config={FUTURE_POTENTIAL_CONFIG}
                                />
                            )}
                        </Box>
                    </Box>
                </Container>
            </Box>
        </ThemeProvider>
    );
}
